import { Center, Stack, Title } from '@mantine/core';
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';
import type { SortingState } from '@tanstack/table-core';
import { useMemo } from 'react';

import { Upload } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { getUserFiles } from '@apple/features/file-manager/api/user-files';
import { FileActions } from '@apple/features/file-manager/components/FileActions';
import { FileInfo } from '@apple/features/file-manager/components/FileInfo';
import type { UserFileModel } from '@apple/features/file-manager/models/models';
import { fileManagerQueryKeys } from '@apple/features/file-manager/queries/queries';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

const defaultFilters: { name?: string } = {};
const defaultSorting: SortingState = [{ id: 'name', desc: false }];

export const Route = createFileRoute('/_authed/_admin/content/file-manager')({
	component: FileManagerRoute,
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'AppleRetail.Security.UserFileRepository.View',
		});

		return {
			userFilesQueryOptions: queryOptions({
				queryKey: fileManagerQueryKeys.queue,
				queryFn: () => getUserFiles(),
				placeholderData: keepPreviousData,
				throwOnError: true,
				initialData: [],
			}),
		};
	},
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient, userFilesQueryOptions } }) => {
		void queryClient.prefetchQuery(userFilesQueryOptions);
	},
});

function FileManagerRoute() {
	const { t } = useTranslation('content', {
		nsMode: 'fallback',
	});

	const { userFilesQueryOptions } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const userFilesQuery = useQuery(userFilesQueryOptions);
	const location = useCurrentLocation();

	const tableState = useTableState<UserFileModel, { name?: string }>({
		search,
		navigate,
		defaultFilters: defaultFilters,
		defaultSorting: defaultSorting,
		fieldMap: [],
	});

	const columnHelper = createDataTableColumnHelper<UserFileModel, UserFileModel>();
	const columns = useMemo(
		() =>
			columnHelper.columns([
				columnHelper.accessor('name', {
					header: t('fileManager.labels.fileName'),
					cell: ({ row }) => <FileInfo userFile={row?.original} />,
					filter: {
						group: t('fileManager.filter.label'),
						variant: 'text',
					},
				}),
				columnHelper.accessor('description', {
					enableColumnFilter: false,
					enableGlobalFilter: false,
					header: t('fileManager.labels.fileDescription'),
				}),
				columnHelper.accessor('id', {
					enableColumnFilter: false,
					enableGlobalFilter: false,
					header: '',
					id: 'id',
					cell: ({ row }) => row && <FileActions documentId={row?.original.id} />,
				}),
			]),
		[columnHelper, t],
	);

	const table = useTable({
		features,
		data: userFilesQuery.data,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		columnResizeMode: 'onChange',
		columns: columns,
	});

	function renderEmpty() {
		if (userFilesQuery.data?.length === 0 && !userFilesQuery.isLoading) {
			return (
				<Stack>
					<Title>{t('fileManager.emptyFileManagerMessage')}</Title>
					<Center>
						<Link to='/content/file-manager/upload' state={{ from: location }}>
							<Title order={3}>{t('fileManager.buttons.uploadFile')}</Title>
						</Link>
					</Center>
				</Stack>
			);
		}
	}

	return (
		<TableLayout
			table={table}
			title={t('fileManager.title')}
			toolbarButtons={[
				<ToolbarButton
					key='reject'
					tooltip={t('fileManager.buttons.uploadFile')}
					icon={Upload}
					onClick={() => {
						void navigate({
							to: '/content/file-manager/upload',
							state: { from: location },
						});
					}}
					data-testid='upload-file-button'
				/>,
			]}
		>
			<DataTable
				table={table}
				loading={userFilesQuery.isFetching}
				renderEmpty={renderEmpty}
			/>
		</TableLayout>
	);
}
